<template>
    <div>
        <feedback-points-control
            v-if="contentTypeId === contentTypeConst.FEEDBACK"
            :feedbackId="feedbackId"
            :drawObject="drawObject"
            @cancel="$_onCancel()"
            @save="$_onStopDrawing"
        />
        <measure-points-control
            v-else-if="contentTypeId === contentTypeConst.MEASUREMENT"
            :feedbackId="feedbackId"
            :drawObject="drawObject"
            @cancel="$_onCancel()"
            @save="$_onStopDrawing"
        />
        <feature-draw
            v-if="getMap"
            :map="getMap"
            :geometryType="feedbackGeometryType"
            :feature="editFeature"
            :geometryTypeChoice="geometryTypeChoice"
            @changed="$_onUpdateDrawing"
            @finish="$_onFinishDrawing"
            ref="drawing"
        />
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import OlPoint from 'ol/geom/Point';
    import OlFeature from 'ol/Feature';
    import OlLineString from 'ol/geom/LineString';

    import {
        CONTENT_TYPE_ID,
        FEEDBACKS_GEOMETRY_TYPES,
        FEEDBACKS_GEOMETRY_TYPE_CODES,
        NG_LAYER_TYPES
    } from '../../../constants';
    import {
        getFeedbackGeometryType,
        getFeatureMeasureTotals,
        getLocationString
    } from '../../../services/feedback';

    import FeatureDraw from '../draw';
    import FeedbackPointsControl from './FeedbackPointsControl.vue';
    import MeasurePointsControl from './MeasurePointsControl.vue';

    export default {
        components: {
            'feature-draw': FeatureDraw,
            'feedback-points-control': FeedbackPointsControl,
            'measure-points-control': MeasurePointsControl
        },

        props: {
            contentTypeId: { type: Number, default: null },
            feedbackId: { type: Number, required: true },
            feedback: { type: Object, default: null },
            isEditing: { type: Boolean, default: false },
            feedbackObject: { type: Object, default: null },
            originalFeedbackGeometryType: { type: String, required: false },
            geometryTypeChoice: { type: String, required: false },
            feedbackType: { type: String, required: false },
            layerId: { type: String, required: false }
        },

        data() {
            return { contentTypeConst: CONTENT_TYPE_ID, drawObject: null };
        },

        computed: {
            ...mapGetters(['getMainMap', 'getFeedbackById', 'getLayerById']),
            editFeature() {
                return this.isEditing
                    ? this.feedbackObject?.featureObject?.feature
                    : null;
            },
            getMap() {
                return this.getMainMap?.map;
            },
            feedbackTheme: function () {
                if (this.feedbackType === NG_LAYER_TYPES.FEEDBACK) {
                    return this.$store.getters.getFeedbackById(this.feedbackId)?.data;
                } else {
                    return this.$store.getters.getLayerById(this.layerId)?.data;
                }
            },
            originalFeedbackGeometryTypeCode() {
                if (!this.originalFeedbackGeometryType) {
                    return null;
                }
                const allCodes = Object.entries(FEEDBACKS_GEOMETRY_TYPE_CODES);
                const allTypes = Object.entries(FEEDBACKS_GEOMETRY_TYPES);
                const currentGeometryObject = allTypes.find(
                    ([key, value]) => value === this.originalFeedbackGeometryType
                );
                const currentCodeDef = allCodes.find(
                    ([key]) => key === currentGeometryObject?.[0]
                );
                return currentCodeDef?.[1];
            },
            feedbackGeometryType: function () {
                if (this.feedbackType === NG_LAYER_TYPES.THEME) {
                    return this.feedbackTheme?.geometryType;
                }
                return (
                    this.originalFeedbackGeometryTypeCode ||
                    this.feedbackTheme?.feedbackType?.geometryType
                );
            },
            isFeedbackMeasure() {
                return this.contentTypeId === CONTENT_TYPE_ID.MEASUREMENT;
            },
            geometryType() {
                return getFeedbackGeometryType(this.feedbackGeometryType);
            }
        },

        methods: {
            $_onUpdateDrawing(drawObject) {
                this.drawObject = drawObject;
            },

            getGeometry(drawObject) {
                const { geometryType, polygon, coordinates } = drawObject;
                if (!(polygon || coordinates)) {
                    return undefined;
                }
                if (geometryType === FEEDBACKS_GEOMETRY_TYPES.POINT) {
                    return new OlPoint(coordinates);
                }
                if (geometryType === FEEDBACKS_GEOMETRY_TYPES.LINE) {
                    return new OlLineString(coordinates);
                }
                if (geometryType === FEEDBACKS_GEOMETRY_TYPES.POLYGON) {
                    return polygon;
                }
                return undefined;
            },

            $_onStopDrawing() {
                this.$_onFinishDrawing(this.drawObject);
            },

            $_onFinishDrawing(drawObject) {
                const { geometryType } = drawObject;
                const isPoint = geometryType === FEEDBACKS_GEOMETRY_TYPES.POINT;
                // After switching from line or polygon drawing to point drawing the points
                // are stored in an array of two points. [[x,y],[x,y]]
                // We pick the element of the array and to store the point
                // geometry as it should be for a point. [x,y]
                if (isPoint) {
                    if (drawObject.coordinates && typeof drawObject.coordinates[0] === 'object') {
                        drawObject.coordinates = [
                            drawObject.coordinates[0][0],
                            drawObject.coordinates[0][1]
                        ];
                    }
                }
                const geometry = this.getGeometry(drawObject);
                const feature = geometry && new OlFeature(geometry);
                const location = feature && getLocationString(feature);
                const featureObject = {
                    geometryType,
                    feature,
                    location
                };
                const measureTotals = getFeatureMeasureTotals(
                    featureObject.feature?.getGeometry(),
                    isPoint
                );
                const feedbackObject = {
                        featureObject,
                        measureTotals
                };
                this.$emit('finished', feedbackObject);
            },

            $_onCancel() {
                this.cancelDrawing();
            },

            cancelDrawing() {
                this.$refs.drawing.stopDrawing();
                this.$emit('cancel');
            }
        },

        watch: {
            geometryType(newVal) {
                this.$emit('updateFeedbackGeometryType', newVal);
            }
        }
    };
</script>
